import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconEdit,
  IconFolder,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type {
  FileOutput,
  FolderOutput,
  GroupClassTypeInput,
} from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { FileUploadModal } from "@/components/FileUploadModal";
import { FolderNavigator } from "@/components/FolderNavigator";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { TrainerRoutes, useNavigator } from "@/routers/routes";
import { specialtyOptions, getSpecialtyLabel } from "./common";

type GroupClassType = GroupClassTypeInput;

export const GroupClassTypes = () => {
  const { data: groupClassTypes } = client.useQuery(
    "get",
    "/trainer/profile/group-class-types",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!groupClassTypes) {
    return <FancyLoader />;
  }

  return <GroupClassTypesList groupClassTypes={groupClassTypes} />;
};

const GroupClassTypesList = (props: { groupClassTypes: GroupClassType[] }) => {
  const { navigate } = useNavigator();

  const handleEdit = (groupClassTypeId: string) => {
    navigate({
      path: TrainerRoutes.PROFILE.GROUP_CLASS_TYPE_EDIT,
      params: { groupClassTypeId },
    });
  };

  const handleAdd = () => {
    navigate({
      path: TrainerRoutes.PROFILE.GROUP_CLASS_TYPE_EDIT,
      params: { groupClassTypeId: "new" },
    });
  };

  const formatPricePer = (pricePer: string) => {
    return pricePer === "SESSION" ? "per session" : "per course";
  };

  return (
    <Stack gap="md">
      <Header
        title="Group Class Types"
        actionControl={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAdd}
            variant="outline"
          >
            Add
          </Button>
        }
      />
      <Alert icon={<IconInfoCircle />} color="blue" variant="light">
        If you want to create group classes that dog owners can join, you need
        to create group class type first. Then you can create a group based on
        this type to set up schedule. Then dog owners will be able to find it
        and request to join.
      </Alert>

      <Stack gap="md">
        {props.groupClassTypes.map((groupClassType) => (
          <Card key={groupClassType.id} withBorder radius="md">
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xl" fw={600}>
                    {groupClassType.name}
                  </Text>
                  {groupClassType.description && (
                    <Text>{groupClassType.description}</Text>
                  )}
                </Stack>
                <Button
                  variant="outline"
                  onClick={() => handleEdit(groupClassType.id)}
                  leftSection={<IconEdit size="1.2em" />}
                >
                  Edit
                </Button>
              </Group>

              <Group gap="md">
                <Text>{groupClassType.durationMinutes} minutes</Text>
                <Text>{groupClassType.sessionCount} sessions</Text>
                <Badge size="lg" variant="filled" color="blue">
                  €{(groupClassType.price / 100).toFixed(2)}{" "}
                  {formatPricePer(groupClassType.pricePer)}
                </Badge>
              </Group>
              {(groupClassType.themes ?? []).length > 0 && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Themes:
                  </Text>
                  {(groupClassType.themes ?? []).map((theme) => (
                    <Badge key={theme} size="sm" variant="light" color="green">
                      {getSpecialtyLabel(theme)}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};

export const GroupClassTypeEdit = () => {
  const { groupClassTypeId } = useParams();
  const { navigate } = useNavigator();
  const isNew = groupClassTypeId === "new";

  const { data: groupClassTypes } = client.useQuery(
    "get",
    "/trainer/profile/group-class-types",
    undefined,
    { staleTime: 0, gcTime: 0 },
  );

  const { data: libraryFiles, refetch: refreshLibraryFiles } = client.useQuery(
    "get",
    "/universal/files/",
    {},
    { staleTime: 0, gcTime: 0 },
  );

  const updateMutation = client.useMutation(
    "post",
    "/trainer/profile/group-class-types",
  );

  if (!groupClassTypes || !libraryFiles) {
    return <FancyLoader />;
  }

  const handleSubmit = async (values: GroupClassType) => {
    const updatedGroupClassTypes = groupClassTypes ? [...groupClassTypes] : [];
    const idx = updatedGroupClassTypes.findIndex((gct) => gct.id === values.id);

    const groupClassType = {
      ...values,
      price: Math.round(values.price * 100),
    };

    if (idx === -1) {
      updatedGroupClassTypes.push(groupClassType);
    } else {
      updatedGroupClassTypes[idx] = groupClassType;
    }

    await updateMutation.mutateAsync({ body: updatedGroupClassTypes });
    navigate({ path: TrainerRoutes.PROFILE.GROUP_CLASS_TYPES });
  };

  const handleDelete = async () => {
    if (!groupClassTypes) return;
    const updated = groupClassTypes.filter(
      (gct) => gct.id !== groupClassTypeId,
    );
    await updateMutation.mutateAsync({ body: updated });
    navigate({ path: TrainerRoutes.PROFILE.GROUP_CLASS_TYPES });
  };

  const existingGroupClassType = groupClassTypes.find(
    (gct) => gct.id === groupClassTypeId,
  );

  const initialValues: GroupClassType = existingGroupClassType
    ? {
        ...existingGroupClassType,
        price: existingGroupClassType.price / 100,
        included: existingGroupClassType.included || [],
        themes: existingGroupClassType.themes || [],
      }
    : {
        id: crypto.randomUUID().replace(/-/g, ""),
        name: "",
        description: "",
        preparation: "",
        durationMinutes: 60,
        sessionCount: 1,
        price: 0,
        pricePer: "SESSION",
        included: [],
        themes: [],
      };

  return (
    <GroupClassTypeEditForm
      isNew={isNew}
      initialValues={initialValues}
      libraryFiles={libraryFiles}
      refreshLibraryFiles={refreshLibraryFiles}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isLoading={updateMutation.isPending}
    />
  );
};

// --- GroupClassTypeEditForm.tsx ---

type Props = {
  isNew: boolean;
  initialValues: GroupClassType;
  libraryFiles?: { files: FileOutput[]; folders: FolderOutput[] };
  refreshLibraryFiles: () => void;
  onSubmit: (values: GroupClassType) => Promise<void>;
  onDelete: () => Promise<void>;
  isLoading?: boolean;
};

const GroupClassTypeEditForm = ({
  isNew,
  initialValues,
  libraryFiles,
  refreshLibraryFiles,
  onSubmit,
  onDelete,
  isLoading = false,
}: Props) => {
  const form = useForm<GroupClassType>({
    name: "group-class-type-form",
    initialValues,
    validate: {
      name: isNotEmpty("Name is required"),
      durationMinutes: isNotEmpty("Duration is required"),
      sessionCount: (value) =>
        value < 1 ? "Session count must be at least 1" : null,
      price: (value) =>
        value < 0 || Number.isNaN(value)
          ? "Price must be a positive number"
          : null,
    },
  });

  const [selectedFileIds, setSelectedFileIds] = useState<string[]>(
    initialValues.fileIds ?? [],
  );
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>(
    initialValues.folderIds ?? [],
  );
  const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const handleSubmit = (values: GroupClassType) => {
    onSubmit({
      ...values,
      fileIds: selectedFileIds,
      folderIds: selectedFolderIds,
    });
  };

  const handleFileSelect = (file: FileOutput) => {
    if (!selectedFileIds.includes(file.id)) {
      setSelectedFileIds((prev) => [...prev, file.id]);
    }
  };

  const handleFolderSelect = (folder: FolderOutput) => {
    if (!selectedFolderIds.includes(folder.id)) {
      setSelectedFolderIds((prev) => [...prev, folder.id]);
    }
  };

  const handleFileRemove = (fileId: string) => {
    setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));
  };

  const handleFolderRemove = (folderId: string) => {
    setSelectedFolderIds((prev) => prev.filter((id) => id !== folderId));
  };

  const onFileUpload = (file: FileOutput) => {
    handleFileSelect(file);
    refreshLibraryFiles();
  };

  const selectedFiles = useMemo(
    () =>
      libraryFiles?.files.filter((file) => selectedFileIds.includes(file.id)) ??
      [],
    [libraryFiles, selectedFileIds],
  );

  const selectedFolders = useMemo(
    () =>
      libraryFiles?.folders.filter((folder) =>
        selectedFolderIds.includes(folder.id),
      ) ?? [],
    [libraryFiles, selectedFolderIds],
  );

  return (
    <Stack>
      <Header
        title={isNew ? "New Group Class Type" : "Edit Group Class Type"}
        actionControl={
          !isNew && (
            <Button
              leftSection={<IconTrash size={16} />}
              variant="outline"
              onClick={openDeleteModal}
            >
              Delete
            </Button>
          )
        }
      />
      <form name="group-class-type-form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. Puppy Socialization Class"
            {...form.getInputProps("name")}
          />

          <TextInput
            label="Description (optional)"
            placeholder="Describe what this group class includes"
            {...form.getInputProps("description")}
          />

          <MultiSelect
            label="Themes (optional)"
            placeholder="Select up to 3 themes"
            data={specialtyOptions}
            maxValues={3}
            {...form.getInputProps("themes")}
            description="This will help clients find your services. Up to 3 themes."
            searchable
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Session duration (minutes)
            </Text>
            <SegmentedControl
              style={{ backgroundColor: "white" }}
              color="blue"
              value={form.values.durationMinutes.toString()}
              onChange={(value) =>
                form.setFieldValue("durationMinutes", parseInt(value))
              }
              data={Array.from({ length: 7 }, (_, i) => (i + 2) * 15).map(
                (duration) => ({
                  label: `${duration}`,
                  value: duration.toString(),
                }),
              )}
              fullWidth
            />
          </Stack>

          <NumberInput
            label="Number of sessions"
            placeholder="How many sessions in this class package?"
            {...form.getInputProps("sessionCount")}
            min={1}
            step={1}
            required={true}
            error={form.errors.sessionCount}
          />

          <NumberInput
            label="Price (€)"
            {...form.getInputProps("price")}
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator=","
            min={0}
            step={0.01}
            required={true}
            error={form.errors.price}
            hideControls
          />

          <Select
            label="Price per"
            placeholder="How is this priced?"
            data={[
              { value: "SESSION", label: "Per session" },
              { value: "COURSE", label: "Per course" },
            ]}
            {...form.getInputProps("pricePer")}
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Total course price
            </Text>
            <Text size="lg" fw="bold">
              €
              {form.values.pricePer === "SESSION"
                ? (form.values.price * form.values.sessionCount).toFixed(2)
                : form.values.price.toFixed(2)}
            </Text>
          </Stack>

          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                What's included in this class? (optional)
              </Text>
              <ActionIcon
                variant="light"
                size="sm"
                onClick={() => {
                  form.insertListItem("included", "");
                }}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
            <Stack gap="xs">
              {(form.values.included || []).map((_, index) => (
                <Group key={index} align="flex-start">
                  <TextInput
                    placeholder="Enter what's included..."
                    style={{ flex: 1 }}
                    key={form.key(`included.${index}`)}
                    {...form.getInputProps(`included.${index}`)}
                  />
                  <ActionIcon
                    color="red"
                    variant="light"
                    onClick={() => {
                      form.removeListItem("included", index);
                    }}
                  >
                    <IconTrash size="1rem" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
            {(form.values.included || []).length === 0 && (
              <Text size="xs" c="dimmed">
                Add items that are included in this group class package
              </Text>
            )}
          </Stack>

          <Divider
            label="Information below is only visible after enrollment"
            labelPosition="center"
          />

          <Textarea
            label="Preparation Instructions (optional)"
            placeholder="What should participants do to prepare for this class?"
            autosize
            minRows={3}
            {...form.getInputProps("preparation")}
          />

          <Stack gap="md">
            <Text fw={500}>Files to share</Text>
            <Group>
              <FolderNavigator
                onFileSelect={handleFileSelect}
                onFolderSelect={handleFolderSelect}
              >
                <Button
                  variant="outline"
                  leftSection={<IconFolder size="1.2em" />}
                  style={{ flex: 1 }}
                >
                  Pick from Library
                </Button>
              </FolderNavigator>
              <FileUploadModal onSuccess={onFileUpload} />
            </Group>

            <Stack gap="md">
              {selectedFolders.map((folder) => (
                <LibraryItem
                  key={folder.id}
                  kind="folder"
                  item={folder}
                  readonly={false}
                  onDelete={() => handleFolderRemove(folder.id)}
                  onFolderClick={() => {}}
                />
              ))}

              {selectedFiles.map((file) => (
                <LibraryItem
                  key={file.id}
                  kind="file"
                  item={file}
                  readonly={false}
                  onDelete={() => handleFileRemove(file.id)}
                />
              ))}
            </Stack>
          </Stack>

          <Button type="submit" fullWidth loading={isLoading}>
            Save Changes
          </Button>
        </Stack>
        <Modal
          opened={deleteModalOpen}
          onClose={closeDeleteModal}
          title="Delete Group Class Type"
        >
          <Stack>
            <Text>
              Are you sure you want to delete this group class type? This action
              cannot be undone.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button color="red" onClick={onDelete} loading={isLoading}>
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </form>
    </Stack>
  );
};
